import { SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import type { TextInputProps } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';

import { InvoiceDetailsDto, InvoiceDetailsUpsertDto } from 'api/dtos/invoiceDetailsDtos';
import { isEmailValid } from 'utils/validationUtils';

export type InvoiceDetailsFormValues = {
    fullName: string;
    invoiceEmail: string;
    country: string;
    street: string;
    buildingNumber: string;
    apartmentNumber: string;
    postalCode: string;
    voivodeship: string;
    city: string;
    companyName: string;
    taxId: string;
    euVatId: string;
};

export const emptyInvoiceDetailsForm: InvoiceDetailsFormValues = {
    fullName: '',
    invoiceEmail: '',
    country: '',
    street: '',
    buildingNumber: '',
    apartmentNumber: '',
    postalCode: '',
    voivodeship: '',
    city: '',
    companyName: '',
    taxId: '',
    euVatId: '',
};

export const normalizeInvoiceDetailsFormValues = (
    values?: Partial<InvoiceDetailsFormValues>,
): InvoiceDetailsFormValues => ({
    ...emptyInvoiceDetailsForm,
    ...values,
});

const requiredInvoiceFields: Array<keyof InvoiceDetailsFormValues> = [
    'fullName',
    'invoiceEmail',
    'country',
    'street',
    'buildingNumber',
    'postalCode',
    'city',
];

export const composeInvoiceStreet = (values: Pick<InvoiceDetailsFormValues, 'street' | 'buildingNumber' | 'apartmentNumber'>) => {
    const streetWithBuilding = [values.street.trim(), values.buildingNumber.trim()]
        .filter(Boolean)
        .join(' ');
    const apartmentNumber = values.apartmentNumber.trim();

    return apartmentNumber ? `${streetWithBuilding}/${apartmentNumber}` : streetWithBuilding;
};

export const toInvoiceDetailsFormValues = (
    details: InvoiceDetailsDto | undefined,
): InvoiceDetailsFormValues => ({
    fullName: details?.fullName ?? '',
    invoiceEmail: details?.invoiceEmail ?? '',
    country: details?.country ?? '',
    street: details?.street ?? '',
    buildingNumber: details?.buildingNumber ?? '',
    apartmentNumber: details?.apartmentNumber ?? '',
    postalCode: details?.postalCode ?? '',
    voivodeship: details?.voivodeship ?? '',
    city: details?.city ?? '',
    companyName: details?.companyName ?? '',
    taxId: details?.taxId ?? '',
    euVatId: details?.euVatId ?? '',
});

export const hasAnyInvoiceDetailsValue = (values: InvoiceDetailsFormValues) => (
    Object.values(values).some((value) => value.trim().length > 0)
);

export const hasCompleteRequiredInvoiceDetails = (values: InvoiceDetailsFormValues) => (
    requiredInvoiceFields.every((field) => values[field].trim().length > 0)
    && isEmailValid(values.invoiceEmail)
);

export const toInvoiceDetailsUpsertDto = (values: InvoiceDetailsFormValues): InvoiceDetailsUpsertDto => ({
    fullName: values.fullName.trim(),
    invoiceEmail: values.invoiceEmail.trim(),
    country: values.country.trim(),
    street: values.street.trim(),
    buildingNumber: values.buildingNumber.trim(),
    apartmentNumber: values.apartmentNumber.trim() || null,
    postalCode: values.postalCode.trim(),
    voivodeship: values.voivodeship.trim() || null,
    city: values.city.trim(),
    companyName: values.companyName.trim() || undefined,
    taxId: values.taxId.trim() || undefined,
    euVatId: values.euVatId.trim() || undefined,
});

type Props = {
    values: InvoiceDetailsFormValues;
    onChange: (field: keyof InvoiceDetailsFormValues, value: string) => void;
    markRequiredFields?: boolean;
    fieldClassNames?: TextInputProps['classNames'];
    showValidationErrors?: boolean;
};

const InvoiceDetailsForm = ({
    values,
    onChange,
    markRequiredFields = true,
    fieldClassNames,
    showValidationErrors = false,
}: Props) => {
    const intl = useIntl();
    const shouldValidate = showValidationErrors && (markRequiredFields || hasAnyInvoiceDetailsValue(values));

    const updateField = (field: keyof InvoiceDetailsFormValues) => (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        onChange(field, event.currentTarget.value);
    };

    const getRequiredError = (field: keyof InvoiceDetailsFormValues) => (
        shouldValidate && values[field].trim().length === 0
            ? intl.formatMessage(messages.requiredField)
            : undefined
    );

    return (
        <Stack gap="lg">
            <Stack gap="sm">
                <Text size="sm" fw={700}>
                    {intl.formatMessage(messages.basicDetails)}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.fullNamePrivate)}
                        value={values.fullName}
                        onChange={updateField('fullName')}
                        error={getRequiredError('fullName')}
                    />
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.invoiceEmail)}
                        value={values.invoiceEmail}
                        onChange={updateField('invoiceEmail')}
                        error={(shouldValidate && values.invoiceEmail.trim().length > 0 && !isEmailValid(values.invoiceEmail))
                            ? intl.formatMessage(messages.invalidEmail)
                            : getRequiredError('invoiceEmail')}
                    />
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.country)}
                        value={values.country}
                        onChange={updateField('country')}
                        error={getRequiredError('country')}
                    />
                </SimpleGrid>
            </Stack>

            <Stack gap="sm">
                <Text size="sm" fw={700}>
                    {intl.formatMessage(messages.addressDetails)}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.street)}
                        value={values.street}
                        onChange={updateField('street')}
                        error={getRequiredError('street')}
                    />
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.buildingNumber)}
                        value={values.buildingNumber}
                        onChange={updateField('buildingNumber')}
                        error={getRequiredError('buildingNumber')}
                    />
                    <TextInput
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.apartmentNumber)}
                        value={values.apartmentNumber}
                        onChange={updateField('apartmentNumber')}
                    />
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.postalCode)}
                        value={values.postalCode}
                        onChange={updateField('postalCode')}
                        error={getRequiredError('postalCode')}
                    />
                    <TextInput
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.voivodeship)}
                        value={values.voivodeship}
                        onChange={updateField('voivodeship')}
                    />
                    <TextInput
                        required={markRequiredFields}
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.city)}
                        value={values.city}
                        onChange={updateField('city')}
                        error={getRequiredError('city')}
                    />
                </SimpleGrid>
            </Stack>

            <Stack gap="sm">
                <Text size="sm" fw={700}>
                    {intl.formatMessage(messages.companyDetails)}
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    <TextInput
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.companyName)}
                        value={values.companyName}
                        onChange={updateField('companyName')}
                    />
                    <TextInput
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.taxId)}
                        value={values.taxId}
                        onChange={updateField('taxId')}
                    />
                    <TextInput
                        classNames={fieldClassNames}
                        label={intl.formatMessage(messages.euVatIdOptional)}
                        value={values.euVatId}
                        onChange={updateField('euVatId')}
                    />
                </SimpleGrid>
            </Stack>
        </Stack>
    );
};

const messages = defineMessages({
    basicDetails: {
        id: 'src.Components.Common.InvoiceDetailsForm.basicDetails',
        defaultMessage: 'Dane podstawowe',
    },
    fullNamePrivate: {
        id: 'src.Components.Common.InvoiceDetailsForm.fullNamePrivate',
        defaultMessage: 'Imię i nazwisko (dla osoby prywatnej)',
    },
    invoiceEmail: {
        id: 'src.Components.Common.InvoiceDetailsForm.invoiceEmail',
        defaultMessage: 'Adres e-mail do faktury',
    },
    country: {
        id: 'src.Components.Common.InvoiceDetailsForm.country',
        defaultMessage: 'Kraj',
    },
    addressDetails: {
        id: 'src.Components.Common.InvoiceDetailsForm.addressDetails',
        defaultMessage: 'Dane adresowe',
    },
    street: {
        id: 'src.Components.Common.InvoiceDetailsForm.street',
        defaultMessage: 'Ulica',
    },
    buildingNumber: {
        id: 'src.Components.Common.InvoiceDetailsForm.buildingNumber',
        defaultMessage: 'Numer budynku',
    },
    apartmentNumber: {
        id: 'src.Components.Common.InvoiceDetailsForm.apartmentNumber',
        defaultMessage: 'Numer lokalu (opcjonalnie)',
    },
    postalCode: {
        id: 'src.Components.Common.InvoiceDetailsForm.postalCode',
        defaultMessage: 'Kod pocztowy',
    },
    voivodeship: {
        id: 'src.Components.Common.InvoiceDetailsForm.voivodeship',
        defaultMessage: 'Województwo',
    },
    city: {
        id: 'src.Components.Common.InvoiceDetailsForm.city',
        defaultMessage: 'Miasto',
    },
    companyDetails: {
        id: 'src.Components.Common.InvoiceDetailsForm.companyDetails',
        defaultMessage: 'Dane firmy',
    },
    companyName: {
        id: 'src.Components.Common.InvoiceDetailsForm.companyName',
        defaultMessage: 'Nazwa firmy',
    },
    taxId: {
        id: 'src.Components.Common.InvoiceDetailsForm.taxId',
        defaultMessage: 'NIP',
    },
    euVatIdOptional: {
        id: 'src.Components.Common.InvoiceDetailsForm.euVatIdOptional',
        defaultMessage: 'VAT UE (opcjonalnie, dla firm z UE)',
    },
    requiredField: {
        id: 'src.Components.Common.InvoiceDetailsForm.requiredField',
        defaultMessage: 'To pole jest wymagane.',
    },
    invalidEmail: {
        id: 'src.Components.Common.InvoiceDetailsForm.invalidEmail',
        defaultMessage: 'Niepoprawny e-mail.',
    },
});

export default InvoiceDetailsForm;
